import React, { useState } from 'react';
import clsx from 'clsx';
import { spotifyApi } from '@/lib/api';
import { useCurrentTrack } from '@/hooks/useCurrentTrack';

/**
 * PlaybackControls Component
 * Standard music player controls: previous, play/pause, next
 */
export const PlaybackControls: React.FC = () => {
  const { isPlaying, refresh } = useCurrentTrack();
  const [isLoading, setIsLoading] = useState(false);

  const handlePlayPause = async () => {
    setIsLoading(true);
    try {
      if (isPlaying) {
        await spotifyApi.playback.pause();
      } else {
        await spotifyApi.playback.play();
      }
      await refresh();
    } catch (error) {
      console.error('Failed to toggle playback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = async () => {
    setIsLoading(true);
    try {
      await spotifyApi.playback.previous();
      await refresh();
    } catch (error) {
      console.error('Failed to skip to previous:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    setIsLoading(true);
    try {
      await spotifyApi.playback.next();
      await refresh();
    } catch (error) {
      console.error('Failed to skip to next:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-center gap-4 md:gap-6">
        {/* Previous */}
        <button
          onClick={handlePrevious}
          disabled={isLoading}
          className={clsx(
            'w-14 h-14 md:w-12 md:h-12 rounded-full bg-gray-700 hover:bg-gray-600 active:bg-gray-500 transition-all flex items-center justify-center',
            { 'opacity-50 cursor-not-allowed': isLoading }
          )}
          title="Previous track"
          aria-label="Previous track"
        >
          <svg
            className="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
          </svg>
        </button>

        {/* Play/Pause */}
        <button
          onClick={handlePlayPause}
          disabled={isLoading}
          className={clsx(
            'w-18 h-18 md:w-16 md:h-16 rounded-full bg-spotify-green hover:bg-spotify-green-light active:bg-spotify-green-dark hover:scale-105 active:scale-95 transition-all flex items-center justify-center shadow-lg shadow-spotify-green/30',
            { 'opacity-50 cursor-not-allowed': isLoading }
          )}
          title={isPlaying ? 'Pause' : 'Play'}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <div className="w-7 h-7 md:w-6 md:h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
          ) : isPlaying ? (
            <svg
              className="w-9 h-9 md:w-8 md:h-8 text-black"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          ) : (
            <svg
              className="w-9 h-9 md:w-8 md:h-8 ml-1 text-black"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Next */}
        <button
          onClick={handleNext}
          disabled={isLoading}
          className={clsx(
            'w-14 h-14 md:w-12 md:h-12 rounded-full bg-gray-700 hover:bg-gray-600 active:bg-gray-500 transition-all flex items-center justify-center',
            { 'opacity-50 cursor-not-allowed': isLoading }
          )}
          title="Next track"
          aria-label="Next track"
        >
          <svg
            className="w-6 h-6"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M16 18h2V6h-2zm-11-7l8.5-6v12z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PlaybackControls;
